import { FluidTheme, FluidThemeInterface } from '@lmig/fluid-core';
import { Component, Element, h, Prop } from '@stencil/core';

import { Utils } from '../../utils/utils';

/**
 * @displayName Divider
 *
 * @contextData Fluid-ized component for the <hr/> HTML element, with
 * properties to customize the style width and color.
 *
 */
@Component({
  tag: 'fluid-divider',
  styleUrl: 'fluid-divider.less',
  shadow: true,
})
export class FluidDivider implements FluidThemeInterface {
  // ================================================================ //
  // -- Host Element

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLElement;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   * @type {string}
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  // ================================================================ //
  // -- Props of varying types

  /**
   * The CSS specified color of what the <hr/> element should be
   * Default to Liberty Dark Grey
   */
  @Prop({ mutable: true }) color = '#707070';

  /**
   * How many pixels the <hr/> will be wide
   */
  @Prop({ mutable: true }) width = 1;

  /**
   * The border-style sets the line style for the border.
   *  Default `solid`
   */
  @Prop({ mutable: true }) borderStyle: 'solid' | 'dashed' = 'solid';

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
  }

  /**
   * Main Render function
   */
  render() {
    const componentClass = {
      'fluid-divider': true,
      [this.theme]: true,
    };

    const componentStyle = {
      border: `${this.width}px ${this.borderStyle} ${this.color}`,
    };

    return <hr class={componentClass} style={componentStyle} />;
  }
}
